import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootReducer } from '@store/index';
import { showAlertError } from '@utils/Alerts/Error';
import { showAlertMessage, showAlertWarning } from '@utils/Alerts/Message';
import { Customer } from '@api/Customers/Customer';
import CustomersService from '@api/Customers/CustomerCtrl';
import PosSessionServ, { PosSessionIdentity } from '@api/PosSessions/PosSessionServ';
import {
    PosSessionCustomerRequest,
    PosSessionLastOrder,
    PosSessionTip,
    PosSessionTotals,
    getPosSessionId
} from '@api/PosSessions/PosSession';
import { PosCart } from '@screens/POS/hooks/usePosCart';

/**
 * El ESPEJO del POS: la sesión viva de caja (`pos-sessions`) que la pantalla
 * `/display` de krispis-web refleja en vivo. Extraído de `POSScreen` en F4 del
 * PLAN-REDISENO-PANEL-POS-Y-RECIBO (§7.0) sin cambiar una regla.
 *
 * Todo lo que sale de aquí es FIRE-AND-FORGET: la venta jamás se bloquea ni se
 * retrasa por el espejo (el `PosSessionServ` se traga sus propios errores).
 *
 * ⚠️ NO MONTAR ESTE HOOK DOS VECES. El debounce del sync y el `lastSessionId`
 * viven a nivel MÓDULO en `PosSessionServ`, bajo el supuesto de que hay un solo
 * POS montado — dos instancias se pisarían el debounce y la limpieza de sesión.
 *
 * ⚠️ El doc de sesión es PÚBLICO (no hay firestore.rules): nunca sale de aquí
 * más que el nombre acortado del cliente, y NUNCA su saldo de puntos.
 */
interface UsePosSessionMirrorParams {
    /** El carrito (de `usePosCart`): de él salen los totales y a él entra la propina. */
    cart: PosCart;
    /** `modalGetCustomer.modalOpen` — el prefill del alta vive solo mientras está abierto. */
    customerModalOpen: boolean;
    /** Abre el alta de cliente cuando el cliente capturó su teléfono en la pantalla. */
    openCustomerModal: () => void;
    /** Asigna el cliente al pedido (la MISMA rutina que usa `ModalGetCustomer`). */
    onAddCustomer: (customer: Customer) => void;
}

const usePosSessionMirror = (params: UsePosSessionMirrorParams) => {
    const { cart, customerModalOpen, openCustomerModal, onAddCustomer } = params;
    const { loggedUser, branchOfficeSelected } = useSelector((state: RootReducer) => state.auth);
    const customersService = useMemo(() => new CustomersService(), []);

    const {
        shoppingCart,
        rewardAmount,
        creditCardAmount,
        bankTransferAmount,
        cashRegisteredAmount,
        tipAmount,
        tipAmountRef,
        applyTip,
        getSubtotal,
        getPromotion,
        getTotal,
        getTotalCash,
        getRemainingToCollect
    } = cart;

    const [sessionTip, setSessionTip] = useState<PosSessionTip | undefined>(undefined);

    const getSessionIdentity = (): PosSessionIdentity | undefined =>
        loggedUser?.uid && branchOfficeSelected?.id
            ? {
                  branchOfficeId: branchOfficeSelected.id,
                  collaboratorId: loggedUser.uid,
                  collaboratorName: loggedUser.profile?.name || ''
              }
            : undefined;

    // El espejo es una pantalla PÚBLICA: de un cliente registrado solo sale su
    // nombre de pila + inicial (D7). El texto libre del cajero pasa tal cual.
    const getCustomerDisplayName = (): string | undefined => {
        const customer = shoppingCart?.customer;
        const name = customer?.profile?.name;
        // '0000' es el walk-in por default: el espejo no tiene a quién saludar
        if (!name || name === '0000') return undefined;
        if (!customer?.uid) return name;
        const first = (customer.profile?.firstName || name).trim().split(/\s+/)[0];
        const lastInitial = (customer.profile?.lastName || '').trim().charAt(0).toUpperCase();
        return lastInitial ? `${first} ${lastInitial}.` : first;
    };

    // El resumen COMPLETO que ve el cajero, para que el espejo pinte el mismo
    // (QA 2026-08-26): mismos importes, mismo orden. La propina se lee del ref
    // porque el confirm puede escribir la sesión antes de que React comprometa
    // el estado.
    const getSessionTotals = (): PosSessionTotals => ({
        subtotal: getSubtotal(),
        promotion: getPromotion(),
        shippingCost: shoppingCart?.shippingCost || 0,
        pointsAmount: rewardAmount,
        total: getTotal(),
        tipAmount: tipAmountRef.current,
        creditCardAmount,
        bankTransferAmount,
        cashRegisteredAmount,
        cashAmount: getTotalCash(),
        remaining: Math.max(0, getRemainingToCollect())
    });

    // El carrito se refleja en `pos-sessions`. Fire-and-forget con debounce
    // dentro del Serv: la venta nunca depende del espejo.
    useEffect(() => {
        const identity = getSessionIdentity();
        if (!identity) return;
        PosSessionServ.syncCart(
            identity,
            {
                // El `id` local del carrito no sale del POS (igual que al persistir)
                products: (shoppingCart?.products || []).map(({ id, ...line }) => line),
                customerName: getCustomerDisplayName(),
                customerHasAccount: !!shoppingCart?.customer?.uid,
                note: shoppingCart?.note
            },
            getSessionTotals()
        );
    }, [
        shoppingCart,
        rewardAmount,
        tipAmount,
        creditCardAmount,
        bankTransferAmount,
        cashRegisteredAmount,
        branchOfficeSelected?.id,
        loggedUser?.uid
    ]);

    // D7: el cliente se identificó (o creó su cuenta) desde el espejo. La
    // suscripción entrega el bloque `customerRequest`; se aplica con la MISMA
    // rutina que ModalGetCustomer (recalcular cartera + asignar). El cajero
    // manda: si el pedido ya tiene cliente con uid, se descarta con aviso. El
    // bloque se limpia SIEMPRE (aplicado o descartado) para no re-procesarlo.
    const [incomingCustomerRequest, setIncomingCustomerRequest] = useState<PosSessionCustomerRequest | undefined>(
        undefined
    );
    const processedCustomerRequestRef = useRef<string>('');
    // Alta desde el espejo (ajuste QA): el cliente capturó su TELÉFONO en la
    // pantalla (no tiene teclado para el nombre) y el alta se abre aquí con el
    // número precargado para que el cajero complete el nombre.
    const [identifyPrefillPhone, setIdentifyPrefillPhone] = useState<string | undefined>(undefined);

    useEffect(() => {
        if (!loggedUser?.uid || !branchOfficeSelected?.id) return;
        const sessionId = getPosSessionId(branchOfficeSelected.id, loggedUser.uid);
        const unsubscribe = PosSessionServ.getPosSessionRealtime(sessionId, (session) => {
            setIncomingCustomerRequest(session?.customerRequest || undefined);
            // F5: la propina que el cliente elige en el espejo, en vivo
            setSessionTip(session?.tip || undefined);
        });
        return () => unsubscribe();
    }, [branchOfficeSelected?.id, loggedUser?.uid]);

    useEffect(() => {
        const request = incomingCustomerRequest;
        if (!request) return;
        // Los snapshots re-entregan el mismo bloque hasta que el clear llega a
        // Firestore: procesar cada solicitud UNA sola vez.
        const requestKey = `${request.uid || request.phone}_${String(
            (request.requestedAt as { seconds?: number })?.seconds ?? request.requestedAt ?? ''
        )}`;
        if (processedCustomerRequestRef.current === requestKey) return;
        processedCustomerRequestRef.current = requestKey;
        const identity = getSessionIdentity();
        if (identity) PosSessionServ.clearCustomerRequest(identity);
        if (shoppingCart?.customer?.uid) {
            showAlertWarning(
                `Se ignoró la identificación de ${
                    request.name || 'la pantalla'
                }: el pedido ya tiene un cliente asignado`
            );
            return;
        }
        // Variante NUEVA: el cliente capturó su teléfono en la pantalla — el
        // cajero completa el nombre en el alta precargada.
        if (request.isNew && request.phone) {
            setIdentifyPrefillPhone(request.phone);
            openCustomerModal();
            showAlertMessage('El cliente capturó su teléfono en la pantalla — completa su nombre');
            return;
        }
        if (!request.uid) return;
        const requestUid = request.uid;
        const applyRequest = async () => {
            try {
                await customersService.correctUserPointsBasedOnOrders(requestUid);
                const customer = await customersService.getCustomer(requestUid);
                onAddCustomer(customer);
                showAlertMessage(`${request.name} se identificó desde la pantalla`);
            } catch (error: any) {
                showAlertError(error);
            }
        };
        void applyRequest();
    }, [incomingCustomerRequest]);

    // El prefill del alta vive solo mientras el modal está abierto
    useEffect(() => {
        if (!customerModalOpen) setIdentifyPrefillPhone(undefined);
    }, [customerModalOpen]);

    // Al desmontar, el carrito local muere con la pantalla: la sesión se limpia
    // (conservando lastOrder) para que el espejo no pinte un pedido que ya no existe.
    useEffect(() => () => PosSessionServ.clearSession(), []);

    // ----- PROPINA: la elige el CLIENTE en su pantalla -----
    // Rediseño 2026-08-26 (4ª ronda de QA): se acabó el paso de propina del
    // cajero — el selector vive al final del resumen del espejo y el cliente lo
    // cierra con "Confirmar orden". Aquí la propina simplemente ESPEJA el
    // bloque `tip` de la sesión: llega, se muestra en el resumen (con su bote
    // de basura) y entra al cobro. Sin bloque = sin propina, en los dos
    // sentidos, así que quitarla con el bote también la limpia allá.
    const announcedTipRef = useRef<string>('');
    useEffect(() => {
        // Sin bloque `tip` en la sesión NO hay decisión del cliente que espejar,
        // así que la propina no se toca. Antes se ponía en 0 sin más, y eso
        // borraba la propina que el cliente de MESA dejó desde su teléfono al
        // pedir la cuenta (F4 del PLAN-SUPERFICIES-CLIENTE-Y-PROPINA): el cajero
        // retomaba la cuenta y la propina desaparecía sola. Quitarla con el bote
        // de basura sigue funcionando — eso deja el bloque en 'pending'.
        if (!sessionTip) return;
        const decided = sessionTip.status === 'selected' || sessionTip.status === 'skipped';
        const amount = decided ? sessionTip.amount || 0 : 0;
        if (amount !== tipAmountRef.current) applyTip(amount);
        // Un aviso por confirmación (los snapshots re-entregan el mismo bloque)
        const key = decided ? `${sessionTip.status}_${amount}_${sessionTip.baseTotal ?? ''}` : '';
        if (key && announcedTipRef.current !== key) {
            announcedTipRef.current = key;
            showAlertMessage(
                amount > 0
                    ? `El cliente confirmó su pedido con $${amount} de propina`
                    : 'El cliente confirmó su pedido (sin propina)'
            );
        }
        if (!key) announcedTipRef.current = '';
    }, [sessionTip, sessionTip?.status, sessionTip?.amount, sessionTip?.baseTotal]);

    // El bote de basura del resumen: quita la propina aquí Y devuelve el
    // selector al cliente (si solo se limpiara aquí, el snapshot la re-aplica).
    const onRemoveTip = () => {
        applyTip(0);
        const identity = getSessionIdentity();
        if (identity) PosSessionServ.clearTip(identity);
    };

    /** La venta cerrada: la pantalla del cliente muestra el folio con su "¡gracias!". */
    const registerLastOrder = (lastOrder: PosSessionLastOrder) => {
        const identity = getSessionIdentity();
        if (identity) PosSessionServ.registerLastOrder(identity, lastOrder);
    };

    /** "Que el cliente lo capture en su pantalla": el espejo abre su teclado de teléfono. */
    const requestIdentify = () => {
        const identity = getSessionIdentity();
        if (identity) PosSessionServ.requestIdentify(identity);
    };

    return {
        identifyPrefillPhone,
        onRemoveTip,
        registerLastOrder,
        requestIdentify
    };
};

export default usePosSessionMirror;
